import * as THREE from 'three';
import type { RoomInfo } from './Rooms';

type Shot = {
  id: string;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Rail start — camera lives here, not on the player. */
  railA: THREE.Vector3;
  /** Rail end — dollies as the player crosses the room. */
  railB: THREE.Vector3;
  /** Axis used to parameterize the rail (player X or Z). */
  railAxis: 'x' | 'z';
  /** Soft pull toward this world point (gate, room heart). */
  interest: THREE.Vector3;
  fov: number;
  lookHeight: number;
  /** Keep the pair inside this working distance (ICO dollies; it does not lose you). */
  bandMin: number;
  bandMax: number;
};

export type CameraUpdateOpts = {
  companion?: THREE.Vector3 | null;
  climbing?: boolean;
  airborne?: boolean;
  zoom?: boolean;
};

/**
 * ICO observer camera.
 *
 * Team Ico did not orbit behind the boy. Each space had a distant vantage
 * (often a short rail). The camera sat there and *swivelled* to keep Ico
 * (or Ico+Yorda) in frame; the player could nudge the view a little and hold
 * R2 to draw closer. Cuts were pans. Composition showed the architecture
 * and the next path — characters stayed small in a long shot.
 *
 * Ueda (GIA, 2001): cameras switch on imaginary lines so you never lose
 * direction. GDC 2004: fixed positions, angle changes as you reach points,
 * pans not cuts. Wikipedia: "fixed but swivels"; small player pan.
 */
export class SoftCamera {
  readonly camera: THREE.PerspectiveCamera;
  /** View yaw for camera-relative move (angle of camera around the look point). */
  yaw = Math.PI;

  private readonly shots: Shot[] = [];
  private active: Shot | null = null;
  private roomBlend = 1;

  private readonly currentPos = new THREE.Vector3();
  private readonly lookAt = new THREE.Vector3();
  private readonly desiredLook = new THREE.Vector3();
  private readonly desiredPos = new THREE.Vector3();
  private readonly focus = new THREE.Vector3();
  private readonly camRight = new THREE.Vector3();
  private readonly camFwd = new THREE.Vector3();
  private initialized = false;

  /** Limited ICO "view surroundings" offset — springs home when released. */
  private panX = 0;
  private panY = 0;
  private zoom = 0;
  private fov = 48;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(42, aspect, 0.25, 320);
  }

  /** Kept so callers can still register the keep; ICO cameras do not pull-in. */
  setOccluders(_root: THREE.Object3D): void {
    /* composition over collision — Team Ico let the lens sit in "illegal" space */
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Author one shot per room. Courtyard gets a far crane; interiors look in
   * from the larger neighbour (through the door) the way ICO staged halls.
   */
  bindLevel(rooms: RoomInfo[], interests?: Record<string, THREE.Vector3>): void {
    this.shots.length = 0;
    for (const room of rooms) {
      this.shots.push(this.makeShot(room, rooms, interests?.[room.id]));
    }
    this.active = this.shots.find((s) => s.id === 'r0') ?? this.shots[0] ?? null;
  }

  /** Mouse / look-pad — small swivel, not a 360 orbit. */
  orbit(dx: number, dy: number): void {
    this.panX = THREE.MathUtils.clamp(this.panX - dx * 0.0014, -0.62, 0.62);
    this.panY = THREE.MathUtils.clamp(this.panY + dy * 0.0010, -0.22, 0.28);
  }

  update(dt: number, target: THREE.Vector3, opts?: CameraUpdateOpts): void {
    const shot = this.pickShot(target);
    if (shot && shot !== this.active) {
      this.active = shot;
      this.roomBlend = 0;
    }
    this.roomBlend = Math.min(1, this.roomBlend + dt * 0.28);

    this.zoom += ((opts?.zoom ? 1 : 0) - this.zoom) * Math.min(1, dt * 2.4);
    this.panX += (0 - this.panX) * Math.min(1, dt * 0.85);
    this.panY += (0 - this.panY) * Math.min(1, dt * 0.85);

    this.composeFocus(target, opts);
    this.composePosition(shot);

    if (!this.initialized) {
      // Arrive from the title vista instead of snapping — a slow ICO pan-in.
      this.currentPos.copy(this.camera.position);
      this.lookAt.copy(this.focus);
      this.initialized = true;
    }

    const arriving = this.roomBlend < 1;
    const posK = arriving ? 0.48 : 0.2;
    const lookK = arriving ? 0.34 : 0.085;
    this.currentPos.lerp(this.desiredPos, 1 - Math.pow(posK, dt));
    this.lookAt.lerp(this.desiredLook, 1 - Math.pow(lookK, dt));

    this.camera.position.copy(this.currentPos);
    this.camera.lookAt(this.lookAt);

    const baseFov = shot?.fov ?? 42;
    const targetFov = THREE.MathUtils.lerp(baseFov, baseFov - 4, this.zoom);
    this.fov += (targetFov - this.fov) * Math.min(1, dt * 3.5);
    if (Math.abs(this.camera.fov - this.fov) > 0.04) {
      this.camera.fov = this.fov;
      this.camera.updateProjectionMatrix();
    }

    this.yaw = Math.atan2(
      this.currentPos.x - this.lookAt.x,
      this.currentPos.z - this.lookAt.z,
    );
  }

  private composeFocus(target: THREE.Vector3, opts?: CameraUpdateOpts): void {
    // ICO frames the space first; the boy is a small figure found inside the composition.
    if (this.active) {
      this.focus.copy(this.active.interest);
      this.focus.y = (this.active.lookHeight ?? 1.0) + 0.55;
    } else {
      this.focus.copy(target);
      this.focus.y += 1.15;
    }

    const subject = target.clone();
    const mate = opts?.companion;
    if (mate) subject.lerp(mate, 0.42);
    const lift = opts?.climbing || opts?.airborne ? 0.55 : 0.15;
    subject.y += lift;

    // Gentle swivel toward the pair — not a chase-cam lock-on.
    const swivel = this.active ? 0.28 : 0.48;
    this.focus.lerp(subject, swivel);

    this.desiredLook.copy(this.focus);
  }

  private composePosition(shot: Shot | null): void {
    if (!shot) {
      this.desiredPos.set(this.focus.x + 4, this.focus.y + 8, this.focus.z - 14);
      return;
    }

    const t = this.railT(shot, this.focus);
    this.desiredPos.lerpVectors(shot.railA, shot.railB, t);

    // Keep a readable long-shot band — ICO dollied along the rail rather than
    // letting Ico walk out of the frame (or into fog) at 20m.
    const minDist = shot.bandMin;
    const maxDist = shot.bandMax;
    const craneY = this.desiredPos.y;
    const gap = this.desiredPos.distanceTo(this.focus);
    if (gap > maxDist) {
      this.desiredPos.lerp(this.focus, 1 - maxDist / gap);
      this.desiredPos.y = craneY;
    } else if (gap < minDist && gap > 0.25) {
      this.desiredPos.addScaledVector(
        this.desiredPos.clone().sub(this.focus).normalize(),
        minDist - gap,
      );
      this.desiredPos.y = craneY;
    }

    // R2-style zoom: a slight dolly — ICO never becomes a shoulder cam.
    if (this.zoom > 0.01) {
      this.desiredPos.lerp(this.focus, this.zoom * 0.16);
    }

    this.camFwd.set(
      this.focus.x - this.desiredPos.x,
      0,
      this.focus.z - this.desiredPos.z,
    );
    if (this.camFwd.lengthSq() < 1e-5) this.camFwd.set(0, 0, 1);
    this.camFwd.normalize();
    this.camRight.set(this.camFwd.z, 0, -this.camFwd.x);

    const dist = this.desiredPos.distanceTo(this.focus);
    this.desiredLook.addScaledVector(this.camRight, this.panX * dist * 0.42);
    this.desiredLook.y += this.panY * dist * 0.28;
  }

  private railT(shot: Shot, p: THREE.Vector3): number {
    let raw: number;
    if (shot.railAxis === 'x') {
      raw = THREE.MathUtils.clamp((p.x - shot.minX) / Math.max(0.1, shot.maxX - shot.minX), 0, 1);
    } else {
      raw = THREE.MathUtils.clamp((p.z - shot.minZ) / Math.max(0.1, shot.maxZ - shot.minZ), 0, 1);
    }
    // Ease + compress travel — camera lives on a short rail, not a dolly track.
    raw = raw * raw * (3 - 2 * raw);
    return THREE.MathUtils.lerp(0.22, 0.78, raw);
  }

  /** Room diagonal — scale ICO working distance to chamber size. */
  private roomSpan(room: RoomInfo): number {
    const sx = room.maxX - room.minX;
    const sz = room.maxZ - room.minZ;
    return Math.hypot(sx, sz);
  }

  private shotBands(room: RoomInfo, outdoor: boolean): { min: number; max: number } {
    const span = this.roomSpan(room);
    if (outdoor) {
      return { min: span * 0.62 + 6, max: span * 0.82 + 10 };
    }
    return { min: span * 0.88 + 4.5, max: span * 1.12 + 7.5 };
  }

  private pickShot(p: THREE.Vector3): Shot | null {
    if (!this.shots.length) return this.active;
    let best: Shot | null = null;
    let bestScore = -1e9;
    for (const shot of this.shots) {
      const pad = 1.05;
      if (p.x < shot.minX - pad || p.x > shot.maxX + pad) continue;
      if (p.z < shot.minZ - pad || p.z > shot.maxZ + pad) continue;
      const inset = Math.min(p.x - shot.minX, shot.maxX - p.x, p.z - shot.minZ, shot.maxZ - p.z);
      const score = inset + (this.active && shot.id === this.active.id ? 0.85 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = shot;
      }
    }
    return best ?? this.active;
  }

  private makeShot(room: RoomInfo, all: RoomInfo[], interest?: THREE.Vector3): Shot {
    const cx = (room.minX + room.maxX) * 0.5;
    const cz = (room.minZ + room.maxZ) * 0.5;
    const outdoor = room.ceilingY - room.floorY > 4.4;
    const bands = this.shotBands(room, outdoor);

    if (outdoor) {
      return {
        id: room.id,
        minX: room.minX,
        maxX: room.maxX,
        minZ: room.minZ,
        maxZ: room.maxZ,
        // High west parapet — whole courtyard reads as one long ICO establishing shot.
        railA: new THREE.Vector3(room.minX + 1.6, 11.2, room.minZ + 2.2),
        railB: new THREE.Vector3(room.minX + 2.4, 10.4, room.maxZ - 2.6),
        railAxis: 'z',
        interest: interest?.clone() ?? new THREE.Vector3(cx, 1.2, room.maxZ - 1.2),
        fov: 40,
        lookHeight: 1.05,
        bandMin: bands.min,
        bandMax: bands.max,
      };
    }

    const lookIn = this.neighbourVantage(room, all);
    if (lookIn) {
      return {
        id: room.id,
        minX: room.minX,
        maxX: room.maxX,
        minZ: room.minZ,
        maxZ: room.maxZ,
        railA: lookIn.railA,
        railB: lookIn.railB,
        railAxis: lookIn.railAxis,
        interest: interest?.clone() ?? new THREE.Vector3(cx, 1.0, cz),
        fov: 42,
        lookHeight: 0.95,
        bandMin: bands.min,
        bandMax: bands.max,
      };
    }

    // Fallback: high interior corner, slightly into the wall (ICO clipped freely).
    const y = Math.min(room.ceilingY - 0.35, room.floorY + 3.35);
    return {
      id: room.id,
      minX: room.minX,
      maxX: room.maxX,
      minZ: room.minZ,
      maxZ: room.maxZ,
      railA: new THREE.Vector3(room.maxX - 0.25, y, room.minZ + 0.35),
      railB: new THREE.Vector3(room.minX + 0.25, y - 0.18, room.minZ + 0.42),
      railAxis: 'x',
      interest: new THREE.Vector3(cx, 0.95, cz),
      fov: 44,
      lookHeight: 0.9,
      bandMin: bands.min,
      bandMax: bands.max,
    };
  }

  /**
   * Sit in the larger adjoining room and look through the doorway —
   * ICO's usual interior staging (camera in the previous volume).
   */
  private neighbourVantage(
    room: RoomInfo,
    all: RoomInfo[],
  ): { railA: THREE.Vector3; railB: THREE.Vector3; railAxis: 'x' | 'z' } | null {
    type Hit = { other: RoomInfo; dir: 'n' | 's' | 'e' | 'w' };
    const hits: Hit[] = [];
    const gap = 1.15;
    const rcx = (room.minX + room.maxX) * 0.5;
    const rcz = (room.minZ + room.maxZ) * 0.5;

    for (const other of all) {
      if (other.id === room.id) continue;
      const overlapX = Math.min(room.maxX, other.maxX) - Math.max(room.minX, other.minX);
      const overlapZ = Math.min(room.maxZ, other.maxZ) - Math.max(room.minZ, other.minZ);
      if (overlapX > 1.1 && Math.abs(other.minZ - room.maxZ) < gap) hits.push({ other, dir: 'n' });
      if (overlapX > 1.1 && Math.abs(room.minZ - other.maxZ) < gap) hits.push({ other, dir: 's' });
      if (overlapZ > 1.1 && Math.abs(other.minX - room.maxX) < gap) hits.push({ other, dir: 'e' });
      if (overlapZ > 1.1 && Math.abs(room.minX - other.maxX) < gap) hits.push({ other, dir: 'w' });
    }
    if (!hits.length) return null;

    hits.sort((a, b) => this.roomRank(b.other) - this.roomRank(a.other));
    const { other, dir } = hits[0];
    const outdoor = other.ceilingY - other.floorY > 4.4;
    const y = outdoor ? 7.8 : Math.min(other.ceilingY - 0.35, other.floorY + 3.15);
    const back = outdoor ? 8.8 : 6.0;
    const side = outdoor ? 3.6 : 2.0;

    if (dir === 'n') {
      const z = other.minZ + Math.min(back, (other.maxZ - other.minZ) * 0.4);
      return {
        railA: new THREE.Vector3(rcx + side, y, z),
        railB: new THREE.Vector3(rcx - side, y - 0.15, z + 0.5),
        railAxis: 'x',
      };
    }
    if (dir === 's') {
      const z = other.maxZ - Math.min(back, (other.maxZ - other.minZ) * 0.4);
      return {
        railA: new THREE.Vector3(rcx + side, y, z),
        railB: new THREE.Vector3(rcx - side, y - 0.15, z - 0.5),
        railAxis: 'x',
      };
    }
    if (dir === 'e') {
      const x = other.minX + Math.min(back, (other.maxX - other.minX) * 0.4);
      return {
        railA: new THREE.Vector3(x, y, rcz + side),
        railB: new THREE.Vector3(x + 0.5, y - 0.15, rcz - side),
        railAxis: 'z',
      };
    }
    const x = other.maxX - Math.min(back, (other.maxX - other.minX) * 0.4);
    return {
      railA: new THREE.Vector3(x, y, rcz + side),
      railB: new THREE.Vector3(x - 0.5, y - 0.15, rcz - side),
      railAxis: 'z',
    };
  }

  private roomRank(room: RoomInfo): number {
    const area = (room.maxX - room.minX) * (room.maxZ - room.minZ);
    const height = room.ceilingY - room.floorY;
    return area + height * 8;
  }
}
